import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { TenantVegetationType } from '../../types/tenantVegetationType';
import { SCOTT_BURGAN_MODEL_CLASS_NAME } from '../../constants/scottBurgan';
import { FBP_MODEL_CLASS_NAME } from '../../constants/fbp';
import { hasSubclass, subclassSpecFor } from '../../constants/modelSubclass';

const MODEL_CLASS_OPTIONS = [
  { value: 'Grassland', label: 'Grassland' },
  { value: 'Savanna', label: 'Savanna' },
  { value: 'Hummock spinifex grasslands', label: 'Hummock spinifex grasslands' },
  { value: 'Buttongrass', label: 'Buttongrass' },
  { value: 'Dry Eucalypt', label: 'Dry Eucalypt' },
  { value: 'Semi-arid mallee heath', label: 'Semi-arid mallee heath' },
  { value: 'Heathland', label: 'Heathland' },
  { value: 'Pine', label: 'Pine' },
  { value: 'Wet Eucalypt', label: 'Wet Eucalypt' },
  { value: 'McArthur', label: 'McArthur' },
  { value: 'Southern Pine', label: 'Southern Pine' },
  { value: 'AFDRS Pine Ensemble', label: 'AFDRS Pine Ensemble' },
  { value: 'Araucaria', label: 'Araucaria' },
  { value: SCOTT_BURGAN_MODEL_CLASS_NAME, label: SCOTT_BURGAN_MODEL_CLASS_NAME },
  { value: FBP_MODEL_CLASS_NAME, label: FBP_MODEL_CLASS_NAME },
  { value: 'Urban', label: 'Urban' },
];

interface FormState {
  veg_class: string;
  max_fuel_load: string;
  accumulation_rate: string;
  model_class_name: string;
  /** Empty string means "no subclass" (``null`` on submit). */
  model_subclass: string;
}

const EMPTY_FORM: FormState = {
  veg_class: '',
  max_fuel_load: '',
  accumulation_rate: '',
  model_class_name: '',
  model_subclass: '',
};

interface Props {
  open: boolean;
  editing: TenantVegetationType | null;
  onClose: () => void;
  onSubmit: (data: {
    veg_class: string;
    max_fuel_load: number;
    accumulation_rate: number;
    model_class_name: string | null;
    model_subclass: number | null;
  }) => void;
  isSaving: boolean;
}

export default function VegetationTypeFormDialog({ open, editing, onClose, onSubmit, isSaving }: Props) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  useEffect(() => {
    if (editing) {
      setForm({
        veg_class: editing.veg_class,
        max_fuel_load: String(editing.max_fuel_load),
        accumulation_rate: String(editing.accumulation_rate),
        model_class_name: editing.model_class_name ?? '',
        model_subclass: editing.model_subclass != null ? String(editing.model_subclass) : '',
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [editing, open]);

  const handleChange = (field: keyof FormState, value: string) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      // Clear any stale subclass when the user picks a class without one.
      if (field === 'model_class_name' && !hasSubclass(value)) {
        next.model_subclass = '';
      }
      return next;
    });
  };

  const handleSubmit = () => {
    onSubmit({
      veg_class: form.veg_class.trim(),
      max_fuel_load: parseFloat(form.max_fuel_load),
      accumulation_rate: parseFloat(form.accumulation_rate),
      model_class_name: form.model_class_name || null,
      model_subclass: form.model_subclass === '' ? null : Number(form.model_subclass),
    });
  };

  const isValid =
    form.veg_class.trim().length > 0 &&
    !isNaN(parseFloat(form.max_fuel_load)) &&
    !isNaN(parseFloat(form.accumulation_rate)) &&
    // Require a subclass when the model class carries its fuel in one.
    (!hasSubclass(form.model_class_name) || form.model_subclass !== '');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? 'Edit Vegetation Type' : 'Add Vegetation Type'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Vegetation Class"
            value={form.veg_class}
            onChange={(e) => handleChange('veg_class', e.target.value)}
            fullWidth
            disabled={!!editing}
          />
          <TextField
            label="Max Fuel Load"
            type="number"
            value={form.max_fuel_load}
            onChange={(e) => handleChange('max_fuel_load', e.target.value)}
            fullWidth
            inputProps={{ step: 'any' }}
          />
          <TextField
            label="Accumulation Rate"
            type="number"
            value={form.accumulation_rate}
            onChange={(e) => handleChange('accumulation_rate', e.target.value)}
            fullWidth
            inputProps={{ step: 'any' }}
          />
          <FormControl fullWidth>
            <InputLabel>Model Class</InputLabel>
            <Select
              value={form.model_class_name}
              label="Model Class"
              onChange={(e) => handleChange('model_class_name', e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {MODEL_CLASS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {(() => {
            const spec = subclassSpecFor(form.model_class_name);
            if (!spec) return null;
            return (
              <FormControl fullWidth>
                <InputLabel>{spec.label}</InputLabel>
                <Select
                  value={form.model_subclass}
                  label={spec.label}
                  onChange={(e) => handleChange('model_subclass', e.target.value)}
                >
                  {spec.options.map((opt) => (
                    <MenuItem key={opt.value} value={String(opt.value)}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            );
          })()}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isValid || isSaving}
        >
          {isSaving ? 'Saving…' : editing ? 'Save Changes' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
